import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Images } from '../../assets';
import { TableSortIcon } from '../../table/table-sort-icon';
import { DynamicSVG } from '../../atoms/dynamic-svg';
import { DocumentActionsDropdown } from '../document-actions-dropdown';
import { ClipLoader } from '../../atoms/clip-loader';
import { TableLoader } from '../../table/table-loader';
import {
  AddNewButton,
  CellDate,
  CellStatus,
  CellTitle,
  CloseButton,
  Dot,
  DotsButton,
  EmptyMessage,
  HeaderButton,
  HeaderRow,
  IconButton,
  ItemRow,
  LoadingWrap,
  MenuAnchor,
  ModalFooter,
  ModalPanel,
  ModalTitle,
  Overlay,
  RowActions,
  StatusBadge,
  TableBlock,
  TableSection,
  TableWrapper
} from './styles';

const getTitleValue = (item, getItemTitle) => {
  if (typeof getItemTitle === 'function') return getItemTitle(item);
  return item?.title || item?.name || 'Document';
};

const getDateValue = (item, getItemDate) => {
  if (typeof getItemDate === 'function') return getItemDate(item);
  return item?.date || item?.addedAt || item?.createdAt || null;
};

const getItemKey = (item, index) => item?.id ?? item?.path ?? index;

const isHiddenFromClient = (item) =>
  item?.hideForClient === true || item?.shouldHideForClient === true;

const getStatusValue = (item) => item?.contractStatus || item?.status || 'to_sign';

// Raw status without the default fallback: null when the contract has no status.
const getRawStatus = (item) => {
  const status = item?.contractStatus ?? item?.status;
  return status == null || status === '' ? null : status;
};

const isSignedStatus = (status) => status === 'sign' || status === 'signed';

const getStatusLabel = (item, intl) => {
  const status = getStatusValue(item);
  if (isSignedStatus(status)) {
    return intl?.formatMessage?.({ id: 'campaign.documents.signed' }) || 'Signed';
  }
  return intl?.formatMessage?.({ id: 'campaign.documents.toSign' }) || 'To sign';
};

const formatAddedDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} | ${hours}:${minutes}`;
};

const ColumnSortIcon = ({ field, sortField, sortOrder }) => (
  <TableSortIcon order={sortField === field ? sortOrder : undefined} />
);

const CreatorItemsModal = ({
  isOpen = false,
  onClose,
  ownerName,
  itemType = 'docs',
  items = [],
  loading = false,
  emptyMessage = 'No items available',
  showAddNew = false,
  addNewLabel = 'Add new',
  addNewLoading = false,
  onAddNew,
  onItemAction,
  onViewItem,
  onToggleVisibility,
  onDownload,
  onEdit,
  onDelete,
  onCopySigningLink,
  getItemTitle,
  getItemDate,
  eyeVisibleIcon,
  eyeHiddenIcon,
  isControlPanel = true,
  intl
}) => {
  const [sortField, setSortField] = useState('added');
  const [sortOrder, setSortOrder] = useState('desc');
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const dropdownButtonRefs = useRef({});
  const dropdownMenuRefs = useRef({});

  const sortedItems = useMemo(() => {
    const resolveSortKey = (item) => {
      if (sortField === 'title') return String(getTitleValue(item, getItemTitle)).toLowerCase();
      if (sortField === 'status') return getStatusValue(item).toLowerCase();
      return new Date(getDateValue(item, getItemDate) || 0).getTime();
    };

    const list = [...items];
    list.sort((a, b) => {
      const left = resolveSortKey(a);
      const right = resolveSortKey(b);
      if (left === right) return 0;
      return sortOrder === 'asc' ? (left > right ? 1 : -1) : left < right ? 1 : -1;
    });
    return list;
  }, [items, sortField, sortOrder, getItemTitle, getItemDate]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  useEffect(() => {
    if (!isOpen) setOpenDropdownKey(null);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownKey == null) return;
      const buttonRef = dropdownButtonRefs.current[openDropdownKey];
      const menuRef = dropdownMenuRefs.current[openDropdownKey];
      const isClickInsideButton = buttonRef?.contains(event.target);
      const isClickInsideMenu = menuRef?.contains(event.target);
      if (!isClickInsideButton && !isClickInsideMenu) {
        setOpenDropdownKey(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownKey]);

  const closeDropdown = () => setOpenDropdownKey(null);

  const wrapAction = (handler, item) => () => {
    closeDropdown();
    handler?.(item);
  };

  if (!isOpen || typeof window !== 'object') return null;

  const isContracts = itemType === 'contracts';
  const title = `${ownerName || 'User'}'s ${isContracts ? 'contracts' : 'docs'}`;

  return createPortal(
    <Overlay onClick={onClose}>
      <ModalPanel onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{title}</ModalTitle>

        <TableBlock>
          {loading ? (
            <LoadingWrap>
              <TableLoader />
            </LoadingWrap>
          ) : sortedItems.length === 0 ? (
            <EmptyMessage>{emptyMessage}</EmptyMessage>
          ) : (
            <TableSection>
              <HeaderRow $hasStatus={isContracts}>
                <HeaderButton type="button" onClick={() => handleSort('title')}>
                  Title <ColumnSortIcon field="title" sortField={sortField} sortOrder={sortOrder} />
                </HeaderButton>
                {isContracts && (
                  <HeaderButton type="button" $align="center" onClick={() => handleSort('status')}>
                    Status{' '}
                    <ColumnSortIcon field="status" sortField={sortField} sortOrder={sortOrder} />
                  </HeaderButton>
                )}
                <HeaderButton
                  type="button"
                  $align={isContracts ? 'right' : 'left'}
                  onClick={() => handleSort('added')}
                  style={isContracts ? { marginRight: 40 } : undefined}
                >
                  Added <ColumnSortIcon field="added" sortField={sortField} sortOrder={sortOrder} />
                </HeaderButton>
                <div />
              </HeaderRow>

              <TableWrapper>
                {sortedItems.map((item, index) => {
                  const itemKey = getItemKey(item, index);
                  const isDropdownOpen = openDropdownKey === itemKey;
                  const hasStatus = getRawStatus(item) != null;
                  const showCopySigningLink =
                    itemType === 'contracts' && Boolean(onCopySigningLink) && hasStatus;

                  return (
                    <ItemRow
                      key={itemKey}
                      $hasStatus={isContracts}
                      $last={index === sortedItems.length - 1}
                    >
                      <CellTitle>{getTitleValue(item, getItemTitle)}</CellTitle>
                      {isContracts && (
                        <CellStatus>
                          {hasStatus && (
                            <StatusBadge $signed={isSignedStatus(getStatusValue(item))}>
                              {getStatusLabel(item, intl)}
                            </StatusBadge>
                          )}
                        </CellStatus>
                      )}
                      <CellDate $hasStatus={isContracts} style={!isContracts ? { marginLeft: 20 } : undefined}>
                        {formatAddedDate(getDateValue(item, getItemDate))}
                      </CellDate>
                      <RowActions>
                        <IconButton
                          type="button"
                          aria-label={
                            isHiddenFromClient(item)
                              ? 'Show to client portal'
                              : 'Hide from client portal'
                          }
                          title={
                            isHiddenFromClient(item)
                              ? 'Show to client portal'
                              : 'Hide from client portal'
                          }
                          onClick={() =>
                            (onToggleVisibility || onViewItem || onItemAction)?.(item)
                          }
                        >
                          <DynamicSVG
                            svgPath={
                              isHiddenFromClient(item) ? eyeHiddenIcon : eyeVisibleIcon
                            }
                            style={{ color: '#797E86' }}
                            width={20}
                            height={20}
                          />
                        </IconButton>
                        <MenuAnchor>
                          <DotsButton
                            type="button"
                            aria-label="More actions"
                            ref={(el) => {
                              if (el) dropdownButtonRefs.current[itemKey] = el;
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownKey(isDropdownOpen ? null : itemKey);
                            }}
                          >
                            <Dot />
                            <Dot />
                            <Dot />
                          </DotsButton>
                          {isDropdownOpen && (
                            <DocumentActionsDropdown
                              buttonRef={dropdownButtonRefs.current[itemKey]}
                              menuRef={(el) => {
                                if (el) dropdownMenuRefs.current[itemKey] = el;
                              }}
                              intl={intl}
                              isControlPanel={isControlPanel}
                              showCopySigningLink={showCopySigningLink}
                              contractType={itemType === 'contracts' ? item?.contractType ?? true : null}
                              contractStatus={item?.contractStatus || item?.status}
                              onCopySigningLink={
                                onCopySigningLink
                                  ? wrapAction(onCopySigningLink, item)
                                  : undefined
                              }
                              onDownload={wrapAction(onDownload || onItemAction, item)}
                              onEdit={onEdit ? wrapAction(onEdit, item) : undefined}
                              onDelete={onDelete ? wrapAction(onDelete, item) : undefined}
                            />
                          )}
                        </MenuAnchor>
                      </RowActions>
                    </ItemRow>
                  );
                })}
              </TableWrapper>
            </TableSection>
          )}

          {showAddNew && onAddNew && (
            <AddNewButton type="button" onClick={onAddNew} disabled={addNewLoading}>
              {addNewLoading ? (
                <ClipLoader size={14} loading color="#0095ff" />
              ) : (
                <DynamicSVG svgPath={Images.addCircle} width={20} height={20} />
              )}
              {addNewLabel}
            </AddNewButton>
          )}
        </TableBlock>

        <ModalFooter>
          <CloseButton type="button" onClick={onClose}>
            Close
          </CloseButton>
        </ModalFooter>
      </ModalPanel>
    </Overlay>,
    document.body
  );
};

export { CreatorItemsModal };
